import { supabase } from './supabase'

/**
 * Email signature and digital business card. SHL-14.
 *
 * ── EVERYTHING THAT MATTERS HERE IS A PURE FUNCTION ─────────────────────────
 *
 * `buildSignatureHtml` and `buildVCard` take a card and return a string. That
 * is not tidiness: an effect does not run under `renderToString`, so anything
 * that generated its output inside a component would be unreachable by the web
 * suite with every assertion passing against an empty string. The route fetches;
 * these two build; the suite tests these two.
 *
 * ── THE SIGNATURE HAS TO SURVIVE BEING PASTED INTO OUTLOOK ──────────────────
 *
 * Which rules out almost everything the hub is built from. No classes, no CSS
 * variables, no flex, no grid, no custom fonts — Figtree is not on the
 * recipient's machine and Gotham is not on anyone's. Tables and inline styles,
 * the way email has always been written. The colours are the brand tokens
 * written out as hex (#004489 is PANTONE 280 C) because a `var()` in a mail
 * client resolves to nothing.
 *
 * ── THE SINGLE MOST IMPORTANT RULE: IT READS CORRECTLY WITH NO IMAGE ────────
 *
 * Outlook and most corporate clients block external images in received mail by
 * default, and a recipient who has never heard of ConeTec is exactly the person
 * whose client blocks hardest. So the logo carries NO information: the wordmark
 * is also set as text, and every contact detail is text. If the image never
 * loads, the signature is still a complete signature with a gap where a logo
 * would be. Do not "simplify" this by putting the name or the company into the
 * image — that is how a signature becomes a red X.
 *
 * ── AND IT IS ALWAYS CONETEC ────────────────────────────────────────────────
 *
 * David, 17 Sep: single brand. There is no brand parameter and there must not
 * be one. `my_signature_card()` does not return the column, so this file could
 * not render Adara even if somebody asked it to — which is the point, because
 * BU 09 carries brand 'Adara' on @conetec.com addresses in the live data today.
 */

/** The brand blue, PANTONE 280 C. Mail clients have no tokens; this is --blue-700. */
const BRAND = '#004489'
/** --black-90. */
const INK = '#1a1a1a'
const MUTED = '#5a5a5a'
const RULE = '#d6d6d6'

/**
 * WHERE THE LOGO LIVES, AND WHY THIS URL IS AN API.
 *
 * `web/public/brand/signature/` in this repo, deployed by Netlify, served from
 * the domain SF owns. Once a signature is in somebody's Outlook it holds this
 * URL for years and refers to it from mail that has left the company, so:
 *
 *   * NEVER rename or delete this path. Replacing the artwork IN PLACE is the
 *     feature — it corrects every signature already sent, which during a
 *     rebrand is exactly what you want.
 *   * NOT Supabase Storage. A storage URL carries the project ref, and
 *     `hhpwyaxgehhtpajzikpw` is stamped `dev` (079) — the environment ADR-0001
 *     says gets reset, branched and handed around. Baking a dev project ref
 *     into ~700 people's outgoing mail, permanently, is the worst available
 *     answer.
 *   * No auth, no signed URL, no expiry. The fetcher is a stranger's mail
 *     client.
 *
 * Served at 2x and sized down by the width/height attributes, so it is not
 * furry on a retina screen.
 */
export const LOGO_URL = 'https://oneconetec.com/brand/signature/conetec.png'
const LOGO_W = 150
const LOGO_H = 32

export type SignatureCard = {
  displayName: string
  givenName: string | null
  familyName: string | null
  jobTitle: string | null
  legalEntity: string | null
  city: string | null
  region: string | null
  country: string | null
  /** 178 (David, 12 Oct): the COMPANY address, or null. Everybody has a card;
   *  a personal address is never on it — the server does not return one. */
  workEmail: string | null
  /** 083's self-entered number. Since 134 it is labelled, and filed, as the MOBILE. */
  workPhone: string | null
  /** 134. Optional so the signature's fixtures need not grow; null = not set. */
  officePhone?: string | null
  /** 134. The person's own office address; null = show the BU's city instead. */
  office?: CardOffice | null
}

/** 134 — an office address a person typed. City and country are required by
 *  the database whenever any of it is set. */
export type CardOffice = {
  street: string | null
  city: string
  region: string | null
  postcode: string | null
  country: string
}

/** Everything the edit form sends — a REPLACE, every field, no defaults (rule 9). */
export type CardDetails = {
  mobile: string | null
  officePhone: string | null
  street: string | null
  city: string | null
  region: string | null
  postcode: string | null
  country: string | null
}

/**
 * Why a card could not be generated, in words the person can act on.
 *
 * The two refusals are ACC-24's two populations and they need two different
 * remedies — ~54 people the company holds no address for, and 178 whose only
 * address is personal. The database raises two distinct messages for exactly
 * this reason; collapsing them here would undo that.
 */
export type CardRefusal = 'no-address' | 'personal-address' | 'unknown'

export function refusalFor(message: string): CardRefusal {
  if (message.includes('no work address on file')) return 'no-address'
  if (message.includes('not a company address')) return 'personal-address'
  return 'unknown'
}

export async function loadSignatureCard(): Promise<SignatureCard> {
  // `.single()` rather than `.select()`: the function returns exactly one row
  // or raises, and there is no version of "zero rows" that is not a bug.
  const { data, error } = await supabase.rpc('my_signature_card').single<{
    display_name: string
    given_name: string | null
    family_name: string | null
    job_title: string | null
    legal_entity: string | null
    city: string | null
    region: string | null
    country: string | null
    work_email: string | null
    work_phone: string | null
  }>()

  if (error) throw error
  // 134's half. A failure here costs the office lines, never the card — the
  // card worked for a fortnight without them.
  const extra = await supabase
    .rpc('my_card_details')
    .single<{ office_phone: string | null; street: string | null; city: string | null; region: string | null; postcode: string | null; country: string | null }>()
  const d = extra.error ? null : extra.data
  return {
    officePhone: d?.office_phone ?? null,
    office: d?.city && d?.country
      ? { street: d.street, city: d.city, region: d.region, postcode: d.postcode, country: d.country }
      : null,
    displayName: data.display_name,
    givenName: data.given_name,
    familyName: data.family_name,
    jobTitle: data.job_title,
    legalEntity: data.legal_entity,
    city: data.city,
    region: data.region,
    country: data.country,
    workEmail: data.work_email,
    workPhone: data.work_phone,
  }
}

/** 134. The whole edit form, one transaction; the mobile goes through 083's setter inside it. */
export async function saveCardDetails(d: CardDetails): Promise<void> {
  const blank = (v: string | null) => (v && v.trim() !== '' ? v.trim() : null)
  const { error } = await supabase.rpc('set_my_card_details', {
    p_mobile: blank(d.mobile),
    p_office_phone: blank(d.officePhone),
    p_street: blank(d.street),
    p_city: blank(d.city),
    p_region: blank(d.region),
    p_postcode: blank(d.postcode),
    p_country: blank(d.country),
  })
  if (error) throw error
}

/** Which of the setter's refusals this is, so the form can say it in the reader's language. */
export function detailsRefusal(message: string): 'phone' | 'address' | 'unknown' {
  if (message.includes('does not look like a phone number')) return 'phone'
  if (message.includes('needs a city and a country')) return 'address'
  return 'unknown'
}

export async function saveWorkPhone(value: string | null): Promise<void> {
  const { error } = await supabase.rpc('set_my_work_phone', { p_value: value })
  if (error) throw error
}

/**
 * "Burnaby, British Columbia" — and it DEGRADES rather than blanking.
 *
 * Measured on the live BU table: 57 of 57 rows carry a country, 54 a region, 50
 * a city. So seven business units would render an empty location line if this
 * demanded all three. The business unit's own NAME is deliberately not an
 * option — the live values are '05 (ConeTec - CA - Toronto)' and
 * '15 (ConeTec - CA - Developement)', carrying a code, a brand prefix and a
 * typo, and `office_name` for BU 09 is 'Adara', which is a brand and not a
 * place.
 */
export function locationLine(card: SignatureCard): string {
  const parts = [card.city, card.region].filter(Boolean) as string[]
  if (parts.length > 0) return parts.join(', ')
  return card.country ?? ''
}

/**
 * The vCard the QR CODE carries — the same contact, written short (134).
 *
 * The code is drawn 156px wide on the card, and every character costs module
 * size. With 134's office phone and street address a full card reached QR
 * version 16 at level Q — 81 modules, under 2px each on a laptop screen. So
 * the code drops what a phone does not need to file the contact (the website
 * URL, the redundant VOICE / INTERNET types) and the downloaded .vcf keeps
 * everything, photo included. Same fields, same order, same escaping.
 */
export function buildQrVCard(card: SignatureCard): string {
  return buildVCard(card)
    .split('\r\n')
    .filter((l) => !l.startsWith('URL:'))
    .map((l) => l.replace('TEL;TYPE=CELL,VOICE:', 'TEL;TYPE=CELL:').replace('TEL;TYPE=WORK,VOICE:', 'TEL;TYPE=WORK:').replace('EMAIL;TYPE=WORK,INTERNET:', 'EMAIL;TYPE=WORK:'))
    .join('\r\n')
}

/**
 * 134 — where the card says you are, as lines. The person's own office address
 * when they have typed one (street / "city, region postcode" / country), and
 * otherwise 083's single BU line, unchanged.
 */
export function placeLines(card: SignatureCard): string[] {
  const o = card.office
  if (o) {
    // Two lines, not three: the card has to fit a laptop window (6 Oct).
    const town = [o.city, [o.region, o.postcode].filter(Boolean).join(' '), o.country].filter(Boolean).join(', ')
    return [o.street, town].filter((x): x is string => !!x && x.trim() !== '')
  }
  const where = locationLine(card)
  return where ? [where] : []
}

/** Escape for HTML attribute and text contexts. Nothing here is trusted markup. */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * The pasteable signature.
 *
 * `tel:` is deliberately stripped of everything but digits and a leading plus —
 * a dialler cannot parse "x21", and a link that dials the wrong number is worse
 * than text. The extension stays in the visible label.
 */
export function buildSignatureHtml(card: SignatureCard): string {
  const where = locationLine(card)
  // Cut at the extension marker FIRST — stripping non-digits alone leaves the
  // extension's own digits welded onto the number ("x21" becomes "21"), which
  // dials a different phone and looks correct in every review.
  const telHref = card.workPhone
    ? card.workPhone.split(/[a-zA-Z#]/)[0].replace(/[^\d+]/g, '')
    : null

  const rows: string[] = []
  rows.push(
    `<td style="padding:0;font:bold 15px/20px Arial,Helvetica,sans-serif;color:${INK};">${esc(
      card.displayName,
    )}</td>`,
  )
  if (card.jobTitle) {
    rows.push(
      `<td style="padding:2px 0 0;font:13px/18px Arial,Helvetica,sans-serif;color:${MUTED};">${esc(
        card.jobTitle,
      )}</td>`,
    )
  }
  // The wordmark AS TEXT, directly under the name. This is the line that makes
  // a blocked image survivable, and it is why it is not inside the logo.
  rows.push(
    `<td style="padding:2px 0 0;font:bold 13px/18px Arial,Helvetica,sans-serif;color:${BRAND};letter-spacing:.02em;">Conetec${
      where ? ` &nbsp;|&nbsp; <span style="font-weight:normal;color:${MUTED};">${esc(where)}</span>` : ''
    }</td>`,
  )

  const contact: string[] = []
  if (card.workPhone) {
    contact.push(
      `<a href="tel:${esc(telHref!)}" style="color:${INK};text-decoration:none;">${esc(
        card.workPhone,
      )}</a>`,
    )
  }
  // 178: no company address, no mailto line. The signature is held until 2027
  // and needs an address to be worth pasting; this keeps the generator total.
  if (card.workEmail) {
    contact.push(
      `<a href="mailto:${esc(card.workEmail)}" style="color:${BRAND};text-decoration:none;">${esc(
        card.workEmail,
      )}</a>`,
    )
  }
  rows.push(
    `<td style="padding:6px 0 0;font:13px/18px Arial,Helvetica,sans-serif;color:${INK};">${contact.join(
      ' &nbsp;·&nbsp; ',
    )}</td>`,
  )

  // The registered entity, small and last. It is not decoration: Lankelma is a
  // GB company and CONETEC PERU SAC is a Peruvian one, and correspondence from
  // either should say so.
  if (card.legalEntity) {
    rows.push(
      `<td style="padding:8px 0 0;border-top:1px solid ${RULE};font:11px/15px Arial,Helvetica,sans-serif;color:${MUTED};">${esc(
        card.legalEntity,
      )}</td>`,
    )
  }

  return [
    `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">`,
    `<tr><td style="padding:0 0 8px;">`,
    // alt carries the wordmark so a blocked image reads as a word, not a box.
    `<img src="${LOGO_URL}" width="${LOGO_W}" height="${LOGO_H}" alt="Conetec" style="display:block;border:0;outline:none;" />`,
    `</td></tr>`,
    ...rows.map((r) => `<tr>${r}</tr>`),
    `</table>`,
  ].join('')
}

/**
 * The vCard, which IS the business card.
 *
 * B21's shape, one step further: the QR encodes this text directly, so there is
 * no hosted card page, no URL, nothing for anon to read and nothing that stops
 * working when the hub does. A client scanning it at a site with no signal
 * still gets a contact.
 *
 * vCard 3.0 rather than 4.0 on purpose — iOS and Android both read 3.0, and 4.0
 * is still the one that occasionally imports as a blank contact.
 *
 * CRLF line endings are not a style choice. RFC 2426 requires them and some
 * Android importers fail closed on bare LF.
 */
export function buildVCard(card: SignatureCard, photoJpegBase64: string | null = null): string {
  const v = (s: string) => s.replace(/([\\;,])/g, '\\$1').replace(/\n/g, '\\n')
  const lines = ['BEGIN:VCARD', 'VERSION:3.0']

  // N is family;given;... and it is the line iPhone NAMES the contact from:
  // it ignores FN, and with N empty and ORG present it files the contact as a
  // company and shows "Conetec" (platform ticket, 11 Oct). On prod 960 of 964
  // people have no given/family name — the roster sync builds display_name
  // from fullName2 and keeps no parts — so the empty N was everybody's card.
  // With no parts, the whole display name goes in the given slot: iPhone then
  // shows exactly the name the card shows. It is not split here, because a
  // split on the last space files a Chilean second surname as the family
  // name, and inventing a structure is worse than not having one.
  const given = card.givenName?.trim() || null
  const family = card.familyName?.trim() || null
  if (given || family) lines.push(`N:${v(family ?? '')};${v(given ?? '')};;;`)
  else lines.push(`N:;${v(card.displayName)};;;`)
  lines.push(`FN:${v(card.displayName)}`)
  lines.push('ORG:Conetec')
  if (card.jobTitle) lines.push(`TITLE:${v(card.jobTitle)}`)
  // 134: the self-entered number is the MOBILE; the office line is its own.
  if (card.workPhone) lines.push(`TEL;TYPE=CELL,VOICE:${v(card.workPhone)}`)
  if (card.officePhone) lines.push(`TEL;TYPE=WORK,VOICE:${v(card.officePhone)}`)
  // 178: only a company address, and only when there is one.
  if (card.workEmail) lines.push(`EMAIL;TYPE=WORK,INTERNET:${v(card.workEmail)}`)
  if (card.office) {
    // 134: the person's own office, street and all.
    const o = card.office
    lines.push(
      `ADR;TYPE=WORK:;;${v(o.street ?? '')};${v(o.city)};${v(o.region ?? '')};${v(o.postcode ?? '')};${v(o.country)}`,
    )
  } else if (card.city || card.region || card.country) {
    // ADR is ;;street;city;region;postcode;country — the platform holds no
    // street address for a person (a stock room is an address, an employee is
    // not, REC/B19) and inventing one would be worse than omitting it.
    lines.push(
      `ADR;TYPE=WORK:;;;${v(card.city ?? '')};${v(card.region ?? '')};;${v(card.country ?? '')}`,
    )
  }
  lines.push('URL:https://www.conetec.com')
  // 132. The face goes in the FILE (Download contact, Share), never in the QR:
  // a 512px JPEG is ~60 KB of base64 and a QR code holds under 3 KB. Callers
  // that draw the code call this without a photo, and the suite checks they do.
  // Folded at 75 characters with CRLF + space (RFC 2426 2.6) — iOS reads a
  // single 80,000-character line, and some Android importers do not.
  if (photoJpegBase64) lines.push(foldLine(`PHOTO;ENCODING=b;TYPE=JPEG:${photoJpegBase64}`))
  lines.push('END:VCARD')
  return lines.join('\r\n')
}

/** RFC 2426 2.6 line folding: 75 characters, then CRLF and one space. */
export function foldLine(line: string): string {
  if (line.length <= 75) return line
  const parts = [line.slice(0, 75)]
  for (let i = 75; i < line.length; i += 74) parts.push(' ' + line.slice(i, i + 74))
  return parts.join('\r\n')
}

/**
 * THE EMAIL SIGNATURE IS HELD UNTIL 2027 (David, 24 Sep OPS-57; 11 Oct).
 *
 * The Brand Transition Q&A v4 answers "Should I update my email signature
 * now?" with "Not yet" (9.4), and One quotes it to everybody. On 24 Sep the
 * generator came off #/profile and a "coming later" card took its place; on
 * 11 Oct David removed the card too: "remove e-sig for now and we'll put this
 * back in in 2027." Nothing on any screen mentions a signature now.
 *
 * The generator stays: buildSignatureHtml above and components/
 * SignaturePreview.tsx. Bringing it back is a code change anyway — Marketing's
 * approved template (docs/email-signature-template.md, 21 Sep) differs from
 * this generator in six places, two of them decisions — so this becomes true
 * in the same commit that adds the section back to #/profile.
 */
export const SIGNATURE_RELEASED = false
